import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, Mail } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { FadeUp, SectionTag } from "../components/site/Reveal";

export default function Login() {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState("email");
  const [busy, setBusy] = useState(false);
  const { refresh } = useAuth();
  const navigate = useNavigate();

  const requestCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/request-code", { email });
      setStage("code");
      toast.success("Sign-in code sent — check your inbox.");
    } catch {
      toast.error("Could not send code. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify", { email, code });
      localStorage.setItem("wb_token", data.token);
      await refresh();
      if (data.user.role === "admin") {
        navigate("/admin");
      } else {
        try {
          const ws = await api.get("/workspaces");
          navigate(ws.data.workspaces.length ? "/workspace" : "/hub");
        } catch { navigate("/hub"); }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="login-page" className="bg-navy min-h-screen pt-16 flex items-center blueprint-grid grain">
      <div className="max-w-md mx-auto w-full px-5 py-24">
        <FadeUp>
          <SectionTag dark>WorkBase Hub // Secure Access</SectionTag>
          <h1 className="font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl mt-5">
            Passwordless sign-in.
          </h1>
          <p className="text-slate-400 text-sm mt-3 leading-relaxed">
            Enter the email you applied or enrolled with. We'll send a one-time code —
            no passwords to remember.
          </p>
        </FadeUp>

        <FadeUp delay={0.15} className="mt-8 bg-charcoal border border-white/10 p-7">
          {stage === "email" ? (
            <form onSubmit={requestCode} data-testid="login-email-form">
              <label className="block text-slate-300 text-sm font-medium mb-2">Email address</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                <input type="email" required data-testid="login-email-input" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@yourbusiness.com"
                  className="w-full bg-navy border border-white/15 text-white pl-10 pr-4 py-3.5 text-sm focus:border-safety outline-none" />
              </div>
              <button type="submit" data-testid="login-send-code-btn" disabled={busy}
                className="w-full mt-5 bg-safety hover:bg-safety-hover disabled:opacity-50 text-white font-semibold py-3.5 transition-colors">
                {busy ? "Sending…" : "Send sign-in code"}
              </button>
            </form>
          ) : (
            <form onSubmit={verify} data-testid="login-code-form">
              <label className="block text-slate-300 text-sm font-medium mb-2">
                6-digit code sent to <span className="text-white">{email}</span>
              </label>
              <div className="relative">
                <KeyRound size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                <input required inputMode="numeric" maxLength={6} data-testid="login-code-input" value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="••••••"
                  className="w-full bg-navy border border-white/15 text-white pl-10 pr-4 py-3.5 text-lg font-mono tracking-[0.5em] focus:border-safety outline-none" />
              </div>
              <button type="submit" data-testid="login-verify-btn" disabled={busy || code.length !== 6}
                className="w-full mt-5 bg-safety hover:bg-safety-hover disabled:opacity-50 text-white font-semibold py-3.5 transition-colors">
                {busy ? "Verifying…" : "Sign in"}
              </button>
              <button type="button" data-testid="login-resend-btn" onClick={() => setStage("email")}
                className="w-full mt-3 text-slate-400 hover:text-white text-sm transition-colors">
                Use a different email
              </button>
            </form>
          )}
        </FadeUp>
        <FadeUp delay={0.25}>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600 mt-6 text-center">
            [Codes expire in 10 minutes // Single use]
          </p>
        </FadeUp>
      </div>
    </div>
  );
}
